import { useCallback, useMemo } from 'react';

import { useAccountContext } from '~context/account';
import { useBotPaywallContext } from '~context/botPaywall';
import { usePaywallContext } from '~context/paywall';
import type { TBotConfigExtended } from '~entities/bot';
import type { TSupportedMessageTypes } from '~entities/messages';
import { CheckoutReason } from '~features/annals';
import { dialogsApi } from '~services/api/dialogs';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';
import { isShowPaid } from '~utils/payments';

export interface PaywallTexts {
	neurons?: string;
	subscription?: string;
}

const paywallTexting = {
	text: {
		neurons: 'Add neurons to view romantic messages',
		subscription: 'Upgrade to access Adult sexting',
	},
	image: {
		neurons: 'Add neurons to view images',
		subscription: 'Upgrade to access Images',
	},
	audio: {
		neurons: 'Add neurons to listen messages',
		subscription: 'Upgrade to listen messages',
	},
	gift: {
		neurons: 'Add neurons to view images',
		subscription: 'Upgrade to access Images',
	},
	pack: {
		neurons: 'Add neurons to buy this set',
		subscription: 'Upgrade to access Images',
	},
	video: {
		neurons: 'Add neurons to buy this video',
		subscription: 'Upgrade to access Exclusive videos',
	},
};

export const useMessageProcessing = (
	paymentType: string[],
	messageContentType: TSupportedMessageTypes,
	isPaid: boolean,
	cost: number,
	userId: string,
	messageId: number,
	bot: TBotConfigExtended,
	isMessageFromInfluencer: boolean
): { paymentRequired: boolean; showPrice?: boolean; action?: () => void } => {
	const { balance, hasSubscription } = useAccountContext();
	const { openPaywall } = usePaywallContext();
	const { openBotPaywall } = useBotPaywallContext();

	const botName = bot.name;
	const botId = bot.clientId.toString();

	const [buyMessage] = dialogsApi.useBuyMessageMutation();
	const paymentRequired = isShowPaid(
		paymentType,
		cost,
		hasSubscription,
		isPaid
	);

	const buy = useCallback(() => {
		return buyMessage({
			recipientId: botId,
			id: userId,
			messageId,
		});
	}, [buyMessage, messageId, botId, userId]);

	const subscribeAndBuy = useCallback(
		(isSuccess?: boolean) => {
			if (!isSuccess) {
				return;
			}

			const unsub = eventsInbox.onTypeMsg(
				EventType.BalanceChanged,
				(payload) => {
					// ignore decrease balance events
					if (payload.change <= 0) {
						return;
					}

					unsub();

					if (payload.balance < cost) {
						return;
					}

					// TODO: process buy error?
					void buy();
				}
			);
		},
		[buy, cost]
	);

	const hasEnoughNeurons = balance >= cost;

	const checkoutReason = useMemo(() => {
		switch (messageContentType) {
			case 'audio':
				return CheckoutReason.ListenAIAudio;
			case 'image':
				return CheckoutReason.WatchAIPhoto;
			case 'video':
				return CheckoutReason.WatchAIVideo;
			default:
				return CheckoutReason.Sexting;
		}
	}, [messageContentType]);

	const onDDCallback = useCallback(
		(isSuccess?: boolean) => {
			if (!isSuccess) {
				return openPaywall({
					prefer: 'neurons',
					texts: paywallTexting[messageContentType],
					botClientId: botId,
					cost,
					checkoutReason,
					callback: subscribeAndBuy,
				})();
			}
			return subscribeAndBuy(isSuccess);
		},
		[
			botId,
			checkoutReason,
			cost,
			messageContentType,
			openPaywall,
			subscribeAndBuy,
		]
	);

	const neuronsPaywallCallback = useCallback(() => {
		return bot.accountType === 'influencer' && !hasSubscription
			? openBotPaywall({ checkoutReason, callback: onDDCallback })
			: openPaywall({
					prefer: messageContentType === 'text' ? undefined : 'neurons',
					texts: paywallTexting[messageContentType],
					botClientId: botId,
					cost,
					checkoutReason,
					callback: subscribeAndBuy,
			  })();
	}, [
		bot.accountType,
		hasSubscription,
		openBotPaywall,
		checkoutReason,
		onDDCallback,
		openPaywall,
		messageContentType,
		botId,
		cost,
		subscribeAndBuy,
	]);

	const purchaseAction = useCallback(() => {
		return hasEnoughNeurons ? void buy() : neuronsPaywallCallback();
	}, [buy, neuronsPaywallCallback, hasEnoughNeurons]);

	const subscriptionAction = useCallback(() => {
		return openPaywall({
			texts: paywallTexting[messageContentType],
			prefer: 'subscription',
			botName,
			checkoutReason,
			botClientId: botId,
		})();
	}, [openPaywall, messageContentType, botName, checkoutReason, botId]);

	const botSubscriptionAction = useCallback(() => {
		return openBotPaywall({ checkoutReason });
	}, [openBotPaywall, checkoutReason]);

	if (paymentType.includes('subscription')) {
		return {
			paymentRequired,
			action: hasSubscription
				? paymentType.includes('purchase')
					? purchaseAction
					: undefined
				: isMessageFromInfluencer
				? botSubscriptionAction
				: subscriptionAction,
			showPrice: hasSubscription,
		};
	}
	if (paymentType.includes('subscription-bot')) {
		return {
			paymentRequired,
			action: hasSubscription
				? paymentType.includes('purchase')
					? purchaseAction
					: undefined
				: botSubscriptionAction,
			showPrice: hasSubscription,
		};
	}

	return {
		paymentRequired,
		action: paymentType.includes('purchase') ? purchaseAction : undefined,
	};
};
